/* ============================================================
   MODULO — CAMPAIGN POPUP
   One controller per [data-modulo-pca] section. Always a center
   modal (scroll-lock + focus-trap).

   What makes this one different from the rest of the popup
   family: an AUDIENCE GATE. Liquid cannot read the query string
   (request exposes path/host/page_type/design_mode/origin/locale
   and nothing else), so "only people who arrived through the
   campaign link" has to be decided here.

   Arriving with the parameter writes a campaign PASS to
   localStorage, so the shopper stays eligible after they click
   through to another page and a delay/exit trigger still fires.

   The shell is adapted from modulo-section-popup-category.js —
   keep the two in sync when touching open/close/focus.
   ============================================================ */
(function () {
  "use strict";
  if (typeof window === "undefined" || typeof document === "undefined") return;

  var REDUCED = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  /* Shopify.designMode is `undefined` outside the editor, never false — so this
     must be a truthiness coercion, not an === comparison. */
  var DESIGN = !!(window.Shopify && window.Shopify.designMode);

  /* ---- body scroll-lock, instance-counted, gutter-compensated ---- */
  var lockCount = 0, savedY = 0;
  function lockScroll() {
    if (lockCount++ === 0) {
      savedY = window.scrollY || window.pageYOffset || 0;
      /* reserve the scrollbar gutter so position:fixed doesn't reflow the page (CLS) */
      var sbw = window.innerWidth - document.documentElement.clientWidth;
      if (sbw > 0) document.body.style.paddingRight = sbw + "px";
      document.body.style.top = -savedY + "px";
      document.body.classList.add("modulo-pca--locked");
    }
  }
  function unlockScroll() {
    if (lockCount > 0 && --lockCount === 0) {
      document.body.classList.remove("modulo-pca--locked");
      document.body.style.top = "";
      document.body.style.paddingRight = "";
      window.scrollTo(0, savedY);
    }
  }

  /* ============================================================
     QUERY STRING
     Hand-parsed rather than URLSearchParams so a decode failure on
     one malformed pair can't throw away the whole lookup.

     Returns null when the key is ABSENT and "" when it is present
     but empty (`?vip`). That distinction is the gate: presence is
     what grants access when no value is required, so `!v` would
     wrongly reject `?vip`.
     ============================================================ */
  function readParam(name) {
    var s = window.location.search;
    if (!s || s.charAt(0) !== "?" || !name) return null;
    var parts = s.slice(1).split("&");
    for (var i = 0; i < parts.length; i++) {
      if (!parts[i]) continue;
      var eq = parts[i].indexOf("=");
      var k = eq < 0 ? parts[i] : parts[i].slice(0, eq);
      try { k = decodeURIComponent(k.replace(/\+/g, " ")); } catch (_) { }
      if (k.toLowerCase() !== name.toLowerCase()) continue;
      var v = eq < 0 ? "" : parts[i].slice(eq + 1);
      try { v = decodeURIComponent(v.replace(/\+/g, " ")); } catch (_) { }
      return v;
    }
    return null;
  }

  /* ============================================================
     STORAGE — campaign pass + frequency cap
     Both are keyed by section id and both swallow storage errors:
     Safari private mode throws on setItem, and a thrown popup is a
     broken page.
     ============================================================ */
  function sid(el) { return el.getAttribute("data-section-id") || "x"; }
  function passKey(el) { return "modulo-pca-pass-" + sid(el); }
  function seenKey(el) { return "modulo-pca-" + sid(el); }

  function readTs(key) {
    try {
      var ts = parseInt(window.localStorage.getItem(key), 10);
      return isNaN(ts) ? 0 : ts;
    } catch (_) { return 0; }
  }
  function writeTs(key) {
    try { window.localStorage.setItem(key, String(Date.now())); } catch (_) { }
  }

  function suppressed(el) {
    var days = parseInt(el.getAttribute("data-frequency"), 10) || 0;
    if (days <= 0) return false;
    var ts = readTs(seenKey(el));
    if (!ts) return false;
    return (Date.now() - ts) < days * 86400000;
  }

  /* ============================================================
     THE GATE
     ============================================================ */
  function eligible(el) {
    if (el.getAttribute("data-audience") !== "campaign") return true;

    var param = (el.getAttribute("data-param") || "").trim();
    /* Fail CLOSED. A blank parameter means the merchant asked to restrict the
       popup but never said how; showing it to everyone would broadcast a
       private offer, which is the harmful direction. Liquid renders an
       editor-only hint explaining this so it never reads as a silent bug. */
    if (!param) return false;

    var want = (el.getAttribute("data-value") || "").trim();
    var got = readParam(param);
    var matched = false;
    if (got !== null) {
      matched = want ? got.toLowerCase() === want.toLowerCase() : true;
    }

    /* isNaN, not `|| 30`: "0" is a legitimate merchant choice meaning
       "this page only", and `||` would silently turn it into 30 days. */
    var passDays = parseInt(el.getAttribute("data-pass"), 10);
    if (isNaN(passDays)) passDays = 30;

    if (matched) {
      /* Written BEFORE the frequency cap is consulted, so an influencer who is
         currently suppressed still banks the pass for later. */
      if (passDays > 0) writeTs(passKey(el));
      return true;
    }

    if (passDays <= 0) return false;
    var ts = readTs(passKey(el));
    if (!ts) return false;
    return (Date.now() - ts) < passDays * 86400000;
  }

  /* ============================================================
     COPY BUTTON
     ============================================================ */
  function wireCopy(root) {
    var btn = root.querySelector("[data-pca-copy]");
    if (!btn) return;
    var lbl = root.querySelector("[data-pca-copylabel]");
    var status = root.querySelector("[data-pca-status]");
    var idle = lbl ? lbl.textContent : "Copy";
    var timer = null;

    function notify() {
      btn.classList.add("is-copied");
      if (lbl) lbl.textContent = "Copied";
      /* The label lives inside the button's own accessible name, so changing it
         is not announced. A separate role="status" node is what actually tells
         a screen-reader user the copy landed. */
      if (status) status.textContent = "Discount code copied";
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        btn.classList.remove("is-copied");
        if (lbl) lbl.textContent = idle;
        if (status) status.textContent = "";
      }, 1900);
    }

    btn.addEventListener("click", function () {
      var code = btn.getAttribute("data-code") || "";
      function fallback() {
        var t = document.createElement("textarea");
        t.value = code;
        t.setAttribute("readonly", "");
        t.style.position = "fixed";
        t.style.opacity = "0";
        document.body.appendChild(t);
        t.select();
        try { document.execCommand("copy"); } catch (_) { }
        t.remove();
        notify();
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(notify, fallback);
      } else {
        fallback();
      }
    });
  }

  /* ============================================================
     POPUP CONTROLLER — open/close, focus trap, ESC
     ============================================================ */
  function wirePopup(sectionEl) {
    var scrim = sectionEl.querySelector("[data-pca-scrim]");
    if (!scrim) return null;
    var dialog = scrim.querySelector("[data-pca-dialog]");
    if (!dialog) return null;
    var closeEls = scrim.querySelectorAll("[data-pca-close]");
    var isOpen = false, lastFocus = null;

    wireCopy(scrim);

    function focusables() {
      return Array.prototype.slice.call(dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(function (el) { return el.offsetParent !== null; });
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocus = document.activeElement;
      scrim.hidden = false;
      scrim.classList.remove("is-closing");
      lockScroll();
      /* a CSS animation runs once per element and will not replay on reopen;
         the offsetWidth read forces a synchronous style flush so it retriggers */
      dialog.style.animation = "none"; void dialog.offsetWidth; dialog.style.animation = "";
      requestAnimationFrame(function () {
        /* focus the close, never the Shop link: a stray Enter would navigate
           away, and it would announce the CTA instead of the offer */
        var first = scrim.querySelector("[data-pca-close]");
        if (first) { try { first.focus({ preventScroll: true }); } catch (_) { first.focus(); } }
      });
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      unlockScroll();
      var done = function () {
        scrim.hidden = true;
        scrim.classList.remove("is-closing");
        if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (_) { } }
      };
      if (REDUCED) { done(); return; }
      scrim.classList.add("is-closing");
      var fired = false;
      var fin = function () { if (fired) return; fired = true; scrim.removeEventListener("transitionend", fin); done(); };
      scrim.addEventListener("transitionend", fin);
      setTimeout(fin, 460);
    }

    /* immediate release (no animation) — theme-editor section teardown */
    function release() { if (isOpen) { isOpen = false; unlockScroll(); } }

    for (var i = 0; i < closeEls.length; i++) closeEls[i].addEventListener("click", close);

    /* mousedown/mouseup are PAIRED deliberately: a click handler alone fires when
       a drag that began inside the dialog happens to release on the scrim, which
       closes the popup out from under someone selecting the discount code. */
    var downOnScrim = false;
    scrim.addEventListener("mousedown", function (e) { downOnScrim = e.target === scrim; });
    scrim.addEventListener("mouseup", function (e) { if (downOnScrim && e.target === scrim) close(); downOnScrim = false; });

    scrim.addEventListener("keydown", function (e) {
      if (scrim.hidden) return;
      if (e.key === "Escape") { e.preventDefault(); close(); return; }
      if (e.key === "Tab") {
        var f = focusables(); if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    return { open: open, close: close, release: release };
  }

  /* ============================================================
     TRIGGERS
     ============================================================ */
  function initSection(sectionEl) {
    if (sectionEl._pcaInit) return;
    sectionEl._pcaInit = true;
    var ctl = wirePopup(sectionEl);
    if (!ctl) return;
    sectionEl._pcaCtl = ctl; /* so a theme-editor section:unload can release its scroll-lock */

    var opened = false;
    function openOnce() { if (opened) return; opened = true; writeTs(seenKey(sectionEl)); ctl.open(); }

    /* theme editor: always preview, ignore both the gate and the frequency cap */
    if (DESIGN) { ctl.open(); return; }

    /* The gate runs FIRST and unconditionally, because arriving through the
       campaign link is what banks the pass — even on a visit where the
       frequency cap will go on to suppress the popup. */
    if (!eligible(sectionEl)) return;
    if (suppressed(sectionEl)) return;

    var trigger = sectionEl.getAttribute("data-trigger") || "delay";
    if (trigger === "page_load") { openOnce(); return; }

    if (trigger === "delay") {
      /* isNaN, not `|| 3`: delay_seconds has min:0, and `||` would silently
         turn a merchant's legitimate 0 into 3. */
      var secs = parseInt(sectionEl.getAttribute("data-delay"), 10); if (isNaN(secs)) secs = 3;
      setTimeout(openOnce, secs * 1000); return;
    }
    if (trigger === "scroll") {
      /* `|| 35` is safe here only because scroll_percent has min:10 */
      var pct = parseInt(sectionEl.getAttribute("data-scroll"), 10) || 35;
      var onScroll = function () {
        var docH = document.documentElement.scrollHeight - window.innerHeight;
        var reached = docH <= 0 ? 100 : ((window.scrollY || window.pageYOffset) / docH) * 100;
        if (reached >= pct) { window.removeEventListener("scroll", onScroll); openOnce(); }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      return;
    }
    if (trigger === "exit") {
      var onOut = function (e) {
        if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) { document.removeEventListener("mouseout", onOut); openOnce(); }
      };
      document.addEventListener("mouseout", onOut);
    }
  }

  function boot() {
    var els = document.querySelectorAll("[data-modulo-pca]");
    for (var i = 0; i < els.length; i++) initSection(els[i]);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  /* theme editor: re-init on add/edit, and release a torn-down section's scroll-lock */
  if (DESIGN) {
    document.addEventListener("shopify:section:load", function (e) {
      var el = e.target && e.target.querySelector ? e.target.querySelector("[data-modulo-pca]") : null;
      if (el) initSection(el);
    });
    document.addEventListener("shopify:section:unload", function (e) {
      var el = e.target && e.target.querySelector ? e.target.querySelector("[data-modulo-pca]") : null;
      if (el && el._pcaCtl && el._pcaCtl.release) el._pcaCtl.release();
    });
  }
})();
